import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL } from '../../utils';
import { Link } from 'react-router-dom';
import { useDataContext } from '../../context/DataContext';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  formatProductStockDisplay,
  getProductMagasinLabel,
  getProductStockQuantity,
} from '../../utils/functions';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const getStockRowClass = (quantity) => {
  if (quantity === 0) return 'duo-table__row--out';
  if (quantity < 10) return 'duo-table__row--low';
  return '';
};

export default function ProductTable() {
  const { categories } = useDataContext();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    category: '',
    subCategory: '',
    brand: '',
    priceRange: '',
    store: '',
  });
  const [draftFilters, setDraftFilters] = useState({
    category: '',
    subCategory: '',
    brand: '',
    priceRange: '',
    store: '',
  });

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [responseMeta, setResponseMeta] = useState(null);
  const [metaExtra, setMetaExtra] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setLoading] = useState(true);

  const [brandsData, setBrandsData] = useState([]);
  const [storesData, setStoresData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [subCategoriesData, setSubCategoriesData] = useState([]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          search: appliedFilters.search || undefined,
          category: appliedFilters.category || undefined,
          sub_category: appliedFilters.subCategory || undefined,
          brand: appliedFilters.brand || undefined,
          price_range: appliedFilters.priceRange || undefined,
          store: appliedFilters.store || undefined,
        },
      });

      setData(response.data.data || []);
      setLastPage(response.data.meta?.last_page || 1);
      setResponseMeta(response.data.meta);
      setMetaExtra(response.data.meta_extra);
    } catch (error) {
      console.error('Erreur lors de la récupération des produits :', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortConfig, appliedFilters]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const token = localStorage.getItem('token');
        const [brandsRes, storesRes] = await Promise.all([
          axios.get(`${APP_API_URL}/marques`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${APP_API_URL}/warehouses`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setBrandsData(brandsRes.data.data || []);
        setStoresData(storesRes.data.data || storesRes.data || []);
      } catch (error) {
        console.error('Erreur filtres produits :', error);
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    if (categories?.length > 0) {
      setCategoriesData(categories.filter((c) => c.type_categorie === null));
      setSubCategoriesData(categories.filter((c) => c.type_categorie !== null));
    }
  }, [categories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => (
        prev.search === searchInput ? prev : { ...prev, search: searchInput }
      ));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const draftSubCategories = draftFilters.category
    ? subCategoriesData.filter((sc) => sc.category_id === Number(draftFilters.category))
    : subCategoriesData;

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) {
      setCurrentPage((p) => p + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      category: draftFilters.category,
      subCategory: draftFilters.subCategory,
      brand: draftFilters.brand,
      priceRange: draftFilters.priceRange,
      store: draftFilters.store,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ category: '', subCategory: '', brand: '', priceRange: '', store: '' });
    setAppliedFilters({
      search: '',
      category: '',
      subCategory: '',
      brand: '',
      priceRange: '',
      store: '',
    });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleRefresh = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchList();
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Liste des Produits', 14, 16);
    const tableRows = data.map((product) => {
      const stock = formatProductStockDisplay(product);
      return [
        product.name,
        product.reference,
        getProductMagasinLabel(product) || '—',
        product?.category?.nom,
        product?.format || 'N/A',
        stock.display,
      ];
    });
    doc.autoTable({
      head: [['Produit', 'Référence', 'Magasin', 'Catégorie', 'Format', 'Stock']],
      body: tableRows,
      startY: 24,
    });
    doc.save('Liste des Produits.pdf');
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      data.map((product) => {
        const stock = formatProductStockDisplay(product);
        return {
          Produit: product.name,
          Référence: product.reference,
          Magasin: getProductMagasinLabel(product) || '—',
          Catégorie: product?.category?.nom,
          Format: product?.format,
          Stock: stock.display,
        };
      })
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Produits');
    writeFile(workbook, 'Liste des Produits.xlsx');
  };

  const handlePrint = () => window.print();

  const rangeFrom = responseMeta?.from ?? 0;
  const rangeTo = responseMeta?.to ?? 0;
  const totalFiltered = responseMeta?.total ?? 0;

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Catalogue</p>
          <h1 className="duo-hero-title">Produits</h1>
          <p className="duo-hero-sub mt-2 mb-0">Gérez votre catalogue et consultez les stocks.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Produits (filtre)</p>
            <p className="duo-kpi-chip__value">{metaExtra?.total ?? 0}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Rupture de stock</p>
            <p className="duo-kpi-chip__value">{metaExtra?.out_of_stock ?? 0}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Stock faible (&lt; 10)</p>
            <p className="duo-kpi-chip__value">{metaExtra?.low_stock ?? 0}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des produits
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setIsOpen(!isOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <Link to="/add-product" className="duo-btn duo-btn--primary">
                  <i className="bi bi-plus" /> Nouveau
                </Link>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par nom ou référence..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <select
                    className="duo-select"
                    value={draftFilters.category}
                    onChange={(e) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        category: e.target.value,
                        subCategory: '',
                      }))
                    }
                  >
                    <option value="">Toutes les catégories</option>
                    {categoriesData.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.nom}
                      </option>
                    ))}
                  </select>
                  <select
                    className="duo-select"
                    value={draftFilters.subCategory}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, subCategory: e.target.value }))}
                  >
                    <option value="">Toutes les sous-catégories</option>
                    {draftSubCategories.map((subCategory) => (
                      <option key={subCategory.id} value={subCategory.id}>
                        {subCategory.nom || subCategory.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="duo-select"
                    value={draftFilters.brand}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, brand: e.target.value }))}
                  >
                    <option value="">Toutes les marques</option>
                    {brandsData.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.nom || brand.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="duo-select"
                    value={draftFilters.priceRange}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, priceRange: e.target.value }))}
                  >
                    <option value="">Tous les prix</option>
                    <option value="0-10000">0 – 10 000 FCFA</option>
                    <option value="10000-50000">10 000 – 50 000 FCFA</option>
                    <option value="50000-100000">50 000 – 100 000 FCFA</option>
                    <option value="100000-1000000">100 000 – 1 000 000 FCFA</option>
                  </select>
                  <select
                    className="duo-select"
                    value={draftFilters.store}
                    onChange={(e) => setDraftFilters((prev) => ({ ...prev, store: e.target.value }))}
                  >
                    <option value="">Tous les magasins</option>
                    {storesData.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('name')}>
                    Produit <SortIcon active={isSortActive('name')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('reference')}>
                    Référence <SortIcon active={isSortActive('reference')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('category_id')}>
                    Catégorie <SortIcon active={isSortActive('category_id')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('format')}>
                    Format <SortIcon active={isSortActive('format')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('stock_quantity')}>
                    Stock <SortIcon active={isSortActive('stock_quantity')} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="duo-empty-row">
                      Chargement des produits…
                    </td>
                  </tr>
                ) : data.length > 0 ? (
                  data.map((product) => {
                    const quantity = getProductStockQuantity(product);
                    const stock = formatProductStockDisplay(product);
                    const magasinLabel = getProductMagasinLabel(product);
                    return (
                      <tr key={product.id} className={getStockRowClass(quantity)}>
                        <td>{product.name}</td>
                        <td>
                          <div>{product.reference || '—'}</div>
                          {magasinLabel && (
                            <div className="text-muted mt-1" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              {magasinLabel}
                            </div>
                          )}
                        </td>
                        <td>{product?.category?.nom || 'N/A'}</td>
                        <td>{product?.format || 'N/A'}</td>
                        <td className="duo-table__stock-desc">
                          <span
                            className={
                              quantity === 0
                                ? 'duo-badge-pill duo-badge-pill--off'
                                : quantity < 10
                                  ? 'duo-badge-pill duo-badge-pill--warn'
                                  : 'duo-badge-pill duo-badge-pill--ok'
                            }
                          >
                            {stock.display}
                          </span>
                        </td>
                        <td>
                          <div className="duo-table__actions">
                            <Link
                              to={`/product-details/${product.id}`}
                              title="Voir"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                            >
                              <i className="bi bi-eye" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="duo-empty-row">
                      Aucun produit correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {responseMeta && (
            <div className="duo-footer-bar">
              <span>
                {isLoading
                  ? 'Chargement…'
                  : `${rangeFrom} – ${rangeTo} sur ${totalFiltered} entrée${totalFiltered > 1 ? 's' : ''}`}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={isLoading || currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {lastPage}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={isLoading || currentPage >= lastPage}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
